import { OrderingStatus, RestaurantStatus } from '@rbi-ctg/store';
import { Stage } from 'generated/rbi-graphql';

import {
  EnvSubtype,
  RBIAppUriScheme,
  RBIBrand,
  RBIEnv,
  RBIExpandedPlatform,
  RBIFullBrandName,
  RBIPlatform,
  RBIReleaseTimestamp,
  Service,
} from './types';

export { kioskUsername } from './kioskValues';

const brandToFullBrandName = {
  [RBIBrand.PLK]: RBIFullBrandName.Popeyes,
  [RBIBrand.TH]: RBIFullBrandName.TimHortons,
  [RBIBrand.BK]: RBIFullBrandName.BurgerKing,
};

const brandToUriScheme = {
  [RBIBrand.PLK]: RBIAppUriScheme.Popeyes,
  [RBIBrand.TH]: RBIAppUriScheme.TimHortons,
  [RBIBrand.BK]: RBIAppUriScheme.BurgerKing,
};

export const brand = () => process.env.REACT_APP_RBI_BRAND as RBIBrand;
export const env = () => (process.env.REACT_APP_RBI_ENV || RBIEnv.DEV) as RBIEnv;
export const graphqlEnv = () => (process.env.REACT_APP_RBI_GRAPHQL_ENV || env()) as RBIEnv;
export const restaurantEnv = () =>
  (process.env.REACT_APP_RBI_RESTAURANT_ENV || env()) as RestaurantStatus & RBIEnv;
export const sanityEnv = () => (process.env.REACT_APP_RBI_SANITY_ENV || env()) as RBIEnv;
export const welcomeEmailDomain = () =>
  (process.env.REACT_APP_RBI_WELCOME_EMAIL_DOMAIN || env()) as Stage;
export const validMobileOrderingEnvs = () =>
  ((process.env.REACT_APP_RBI_VALID_MOBILE_ORDERING_ENVS as string) || '').split(
    ','
  ) as OrderingStatus[];
export const fullBrandName = () => brandToFullBrandName[brand()];
export const appUriScheme = () => brandToUriScheme[brand()];
export const platform = () => process.env.REACT_APP_RBI_PLATFORM as RBIPlatform;
export const releaseTimestamp = () =>
  process.env.REACT_APP_RELEASE_TIMESTAMP as RBIReleaseTimestamp;
export const appVersionCode = () => process.env.CIRCLE_SHA1 || process.env.COMMIT_REF || 'dev';

export const checkPlatform = (is: RBIPlatform) => is === platform();
export const checkRbiEnv = (is: RBIEnv) => is === env();

export const isLocalDev = process.env.NODE_ENV !== 'production';
export const isTest = process.env.NODE_ENV === 'test';
export const isProduction = checkRbiEnv(RBIEnv.PROD);
export const isWeb = checkPlatform(RBIPlatform.WEB);
export const isKiosk = checkPlatform(RBIPlatform.KIOSK);
export const isNative = checkPlatform(RBIPlatform.APP);
export const isMobile = () => /Mobile/.test(navigator.userAgent);
export const isNativeIOS = () => isNative && /iPad|iPhone|iPod/.test(navigator.userAgent);
export const firstDataUrl = process.env.REACT_APP_RBI_FIRSTDATA_URL as string;
export const firstDataUrlTh = process.env.REACT_APP_RBI_FIRSTDATA_URL_TH as string;
export const thDigitalUrl = process.env.REACT_APP_RBI_TH_DIGITAL_URL as string;
export const country = process.env.REACT_APP_RBI_COUNTRY as string;
export const sanityProjectId = process.env.REACT_APP_RBI_SANITY_PROJECT_ID || 'czqk28jt';
// Our Dataset is always of the form ENV_BRAND except in test ENV where we always use 'automation'
export const sanityDataset = process.env.REACT_APP_SANITY_DATASET || `${sanityEnv()}_${brand()}`;

export const appleMerchantId = process.env.REACT_APP_RBI_APPLE_MERCHANT_ID || '';
export const applePrepaidMerchantId = process.env.REACT_APP_RBI_APPLE_PREPAID_MERCHANT_ID || '';
export const applePaymentsNetworks = process.env.REACT_APP_RBI_APPLE_PAYMENTS_NETWORKS
  ? process.env.REACT_APP_RBI_APPLE_PAYMENTS_NETWORKS.split(',')
  : [];

export const googlePaymentsGateway = process.env.REACT_APP_RBI_GOOGLE_PAYMENTS_GATEWAY || '';
export const googlePaymentsGatewayMerchantIdCA =
  process.env.REACT_APP_RBI_GOOGLE_PAYMENTS_GATEWAY_MERCHANT_ID_CA || '';
export const googlePaymentsGatewayMerchantIdUS =
  process.env.REACT_APP_RBI_GOOGLE_PAYMENTS_GATEWAY_MERCHANT_ID_US || '';

export const googlePrepaidPaymentsGateway =
  process.env.REACT_APP_RBI_GOOGLE_PREPAID_PAYMENTS_GATEWAY || '';
export const googlePrepaidPaymentsGatewayMerchantIdCA =
  process.env.REACT_APP_RBI_GOOGLE_PREPAID_PAYMENTS_GATEWAY_MERCHANT_ID_CA || '';
export const googlePrepaidPaymentsGatewayMerchantIdUS =
  process.env.REACT_APP_RBI_GOOGLE_PREPAID_PAYMENTS_GATEWAY_MERCHANT_ID_US || '';

export const googlePaymentsNetworks = process.env.REACT_APP_RBI_GOOGLE_PAYMENTS_NETWORKS
  ? process.env.REACT_APP_RBI_GOOGLE_PAYMENTS_NETWORKS.split(',')
  : [];
export const googlePaymentsEnvironment =
  process.env.REACT_APP_RBI_GOOGLE_PAYMENTS_ENVIRONMENT || '';

export const TokenExPublicKey = process.env.REACT_APP_RBI_TOKENEX_PUBLIC_KEY || '';

export const CognitoEnvironmentValues = {
  awsRegion: () => process.env.REACT_APP_RBI_AWS_REGION!,
  userPoolId: () => process.env.REACT_APP_USER_POOL_ID!,
  userPoolClientId: () => process.env.REACT_APP_USER_POOL_CLIENT_ID!,
};

export { EnvSubtype, RBIBrand, RBIEnv, RBIPlatform, Service, RBIExpandedPlatform };
export { default as ApiKeys } from './api-keys';

export const DataDog = {
  logs: {
    ClientToken: process.env.REACT_APP_DATADOG_RUM_CLIENT_TOKEN,
  },
  RUM: {
    ApplicationId: process.env.REACT_APP_DATADOG_RUM_APPLICATION_ID,
    ClientToken: process.env.REACT_APP_DATADOG_RUM_CLIENT_TOKEN,
  },
};

export const googleTagManagerScriptId = () =>
  `rbi-${brand()}-${country}-${env()}-google-tag-manager`;
